// PLEX-5920: the title and description the app already has for a cited URL, so its source card
// shows them instead of fetching the page's own metadata, which is generic for some sites (every
// Open Targets disease page says "Open Targets Platform") and blocked for others (PubMed answers
// with a CAPTCHA).
//
// Two places have them:
//  - a Plex-ID link resolved by interpolateSourceLinks: the entity's name and description
//    (SOURCE_ENTITY);
//  - an evidence tool result (fetch_evidence, fetch_direct_relationships): its source_metadata,
//    {url: {title, description}}, the edges' title and description before the backend combined
//    them into the record text the model cites (SOURCE_EDGE). Only the search analyst has those
//    tools, so these results are in sub-agent turns, which sourceMetadataFromClient walks.

export const SOURCE_ENTITY = "entity";
export const SOURCE_EDGE = "edge";

const MAX_DESCRIPTION = 500;

/** Plain text for a card from a description, which may carry Markdown or HTML. */
export const plainDescription = (text) => {
  if (!text || typeof text !== "string") {
    return null;
  }
  const plain = text
    .replace(/\[([^\]]*)\]\((?:[^()]|\([^)]*\))*\)/g, "$1")   // [label](url), url may hold (...)
    .replace(/<[^>]+>/g, " ")                                 // tags
    .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"')
    .replace(/(^|\s)[*•]\s+/g, "$1")                          // bullets
    .replace(/(\*\*|__)(.*?)\1/g, "$2")                       // bold
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) {
    return null;
  }
  return plain.length > MAX_DESCRIPTION ? `${plain.slice(0, MAX_DESCRIPTION - 1).trimEnd()}…` : plain;
};

/** {url: {title, description}} from one tool result's source_metadata, first entry per URL winning. */
export const sourceMetadataFromToolResult = (toolResult, out = {}) => {
  for (const item of toolResult?.content || []) {
    const metadata = item?.json?.source_metadata;
    if (metadata && typeof metadata === "object") {
      for (const [url, meta] of Object.entries(metadata)) {
        if (!(url in out)) out[url] = meta;
      }
    }
  }
  return out;
};

/**
 * {url: {title, description}} from every tool result in a list of turns ({content: [blocks]}),
 * including the sub-agent turns a reloaded conversation keeps in a turn's agentMessagesCache.
 */
export const sourceMetadataFromTurns = (turns, out = {}) => {
  for (const turn of Array.isArray(turns) ? turns : []) {
    for (const block of turn?.content || []) {
      if (block?.toolResult) {
        sourceMetadataFromToolResult(block.toolResult, out);
      }
    }
    const cache = turn?.agentMessagesCache;
    if (cache && typeof cache === "object") {
      for (const subTurns of Object.values(cache)) sourceMetadataFromTurns(subTurns, out);
    }
  }
  return out;
};

/** Every tool result's source_metadata in a chat: its turns and its live sub-agents' turns. */
export const sourceMetadataFromClient = (client) => {
  const out = {};
  if (!client) return out;
  sourceMetadataFromTurns(client.turns, out);
  const subAgentTurns = client.subAgentTurns;
  if (subAgentTurns && typeof subAgentTurns.forEach === "function") {
    subAgentTurns.forEach((subTurns) => sourceMetadataFromTurns(subTurns, out));
  } else if (subAgentTurns && typeof subAgentTurns === "object") {
    for (const subTurns of Object.values(subAgentTurns)) sourceMetadataFromTurns(subTurns, out);
  }
  return out;
};
